import { BUSINESS_TIMEZONE, toDecimal } from '@mm/shared';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const monthName = (m: number, long = false) => (long ? MONTHS_LONG : MONTHS)[m - 1] ?? '';

/**
 * Format a decimal string as money using Indian digit grouping (₹1,18,000.00).
 * The value is rounded with Decimal first, so no float math is involved.
 */
export function formatMoney(
  value: string | null | undefined,
  currency = 'INR',
  fractionDigits = 2,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const fixed = toDecimal(value).toFixed(fractionDigits);
  const negative = fixed.startsWith('-');
  const [intPart = '0', frac] = fixed.replace('-', '').split('.');
  const grouped = currency === 'INR' ? groupIndian(intPart) : groupWestern(intPart);
  const symbol = CURRENCY_SYMBOLS[currency] ?? `${currency} `;
  return `${negative ? '-' : ''}${symbol}${grouped}${frac ? `.${frac}` : ''}`;
}

const CURRENCY_SYMBOLS: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };

function groupWestern(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** 118000 → 1,18,000 ; 12345678 → 1,23,45,678 */
function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${rest},${last3}`;
}

/** Plain decimal with trailing zeros trimmed: '88.500000' → '88.5'. */
export function formatDecimal(value: string | null | undefined, maxDp = 6): string {
  if (value === null || value === undefined || value === '') return '—';
  return toDecimal(value).toDecimalPlaces(maxDp).toString();
}

export function formatPercent(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return `${formatDecimal(value, 2)}%`;
}

/** '2026-10-01' → '01 Oct 2026' without touching timezones. */
export function formatDate(ymd: string | null | undefined): string {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-');
  return `${d} ${monthName(Number(m))} ${y}`;
}

/** ISO timestamp → IST date-time. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: BUSINESS_TIMEZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}
